/*
 * Relewise collection cards — client-side renderer.
 *
 * Builds the SAME markup snippets/product-card.liquid renders (via
 * snippets/_relewise-product-card.liquid), from structured data instead of a
 * per-product `?view=relewise-product-card` fetch. relewise.js owns the data:
 * it decodes the Relewise result into a card model, renders it at once, and
 * re-renders with the background card data (templates/search.relewise-card-data
 * .liquid) when that lands. This file only turns (model, context) into HTML —
 * no fetches, no DOM reads — so it can be unit-tested under Node.
 *
 * Ported from the research renderer in PR #575 (qa/relewise-performance/
 * renderer.js), which was pixel-compared against the Liquid card on 48 products
 * at desktop/mobile, rest/hover. Keep it a faithful port: any change to
 * product-card.liquid, card-price.liquid, price.liquid, product-label.liquid,
 * _card-swatches.liquid or _energy-label.liquid that alters card markup needs
 * the matching change here. The Liquid card stays the oracle.
 *
 * Deliberate differences from the Liquid card, both invisible:
 * - In `card_colors_style: native` Liquid also emits every other product image
 *   as a hidden <img>. Those are read only by ProductCard#handleSwatchChange on
 *   a `.opt-btn` change, and a card with no native swatch values has none, so
 *   they are omitted. Cards WITH native swatch values are unsupported (below).
 * - No quick-add control until the model is `purchaseKnown` (template suffix
 *   and has_only_default_variant arrived with the background data): a
 *   configurator product must never flash a buy button.
 *
 * Context (ctx) — built once per page by snippets/relewise-card-context.liquid:
 *   settings, translations, moneyFormat, moneyWithCurrencyFormat, sizes,
 *   mixAndSaveConfiguration, icons {wishlist, addToCart, placeholder},
 *   energyFiles {A..G: url|null}. Optional `today` (YYYY-MM-DD) pins the
 *   schedule day in tests; the page leaves it unset (Europe/Copenhagen today).
 *
 * Model (p) — built per product by relewise.js (rlwCardModel):
 *   id, handle, url, title, titleLine1, titleLine2, type, templateSuffix,
 *   available, hasOnlyDefaultVariant, purchaseKnown, highlight, customBadge,
 *   newLabel, fabricSwatches [url], colorCount, colorsCountText,
 *   energy {class, fullsizeUrl, datasheetUrl}|null,
 *   media {hero, hover, slides?, quality} with images {id?, src, alt, packAlt,
 *   width, aspectRatio} — slides = the touch-swipe pictures after the hero
 *   (snippets/_card-swipe-slides.liquid's list, at most 4),
 *   variant {id, price, compareAtPrice (cents|null), available, schedule,
 *   fastLowPrice, mixAndSave, campaigns [{id, label, endDate, icon, color}],
 *   inventoryTracked, inventoryPolicy (background data only)}.
 */
(function (root) {
  'use strict';

  const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const text = (ctx, key) => ctx.translations[key] || '';
  const tr = (ctx, key) => esc(text(ctx, key));

  // price-as-money.liquid: the shop money format, zero cents dropped, nonzero
  // cents kept, optional superscript cents.
  function money(cents, ctx, currency = ctx.settings.show_currency_code) {
    const format = (currency ? ctx.moneyWithCurrencyFormat : ctx.moneyFormat) || '{{amount_with_comma_separator}} kr.';
    const token = (format.match(/{{\s*(\w+)\s*}}/) || [, 'amount'])[1];
    const comma = !token.includes('period_and_space_separator') && (token.includes('comma_separator') || token.includes('space_separator'));
    const decimal = comma ? ',' : '.';
    const thousands = token.includes('space_separator') ? ' ' : token.includes('apostrophe') ? "'" : comma ? '.' : ',';
    const noDecimals = token.includes('no_decimals');
    let [whole, fraction] = (Number(cents || 0) / 100).toFixed(noDecimals ? 0 : 2).split('.');
    whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    let result = format.replace(/{{\s*\w+\s*}}/, whole + (fraction && fraction !== '00' ? decimal + fraction : ''));
    // Trusted shop configuration, not product-supplied HTML — same as Liquid.
    if (ctx.settings.superscript_decimals && !ctx.moneyFormat.includes('money') && !ctx.moneyFormat.includes('.') && result.includes(decimal)) {
      result = result.split(decimal).join('<sup>');
      result = currency ? result.replace(/ ([^ ]*)$/, '</sup> $1') : result + '</sup>';
    }
    return result;
  }

  // Today in Europe/Copenhagen, the timezone the feed is keyed in — the same
  // fallback as cardTodayCopenhagen (main.js). ctx.today pins it in tests.
  function today(ctx) {
    if (ctx.today) return ctx.today;
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Copenhagen' }).format(new Date());
    } catch (e) {
      return new Date().toISOString().slice(0, 10);
    }
  }

  // The schedule day for today: today's row, else the latest row before today
  // (Q2 option A, last known value), none when every row is in the future —
  // a port of cardResolveScheduleDay (main.js) and _schedule-day-key.liquid,
  // both feed shapes. It used to take the schedule's generatedAt day — when AX
  // last wrote the feed, not today — so ended member prices stayed on the
  // collection grid (ClickUp 869fbtgaa).
  function scheduleDay(days, todayKey) {
    if (!days || typeof days !== 'object') return null;
    if (Array.isArray(days)) {
      let latest = null;
      for (const d of days) {
        if (d && d.date && d.date <= todayKey && (!latest || d.date > latest.date)) latest = d;
      }
      return latest;
    }
    if (days[todayKey]) return days[todayKey];
    const past = Object.keys(days).filter(k => k <= todayKey).sort();
    return past.length ? days[past[past.length - 1]] : null;
  }

  const digits = x => String(x ?? '').replace(/\D/g, '');

  // card-price.liquid's schedule state, with the two resolveScheduleState
  // exceptions: a member row whose own campaignEndDate is before today is not
  // a member day, and a regular row whose campaign has ended — its
  // campaignEndDate, else the matched campaign's endDate — is no row at all
  // (no label, percent, special price or stock note; the catalog sale badge
  // stays). `regular` / `campaign` are the guarded pair: read them, never
  // day.regular (qa/price-schedule/day-rule.test.cjs checks).
  function state(p, ctx) {
    const v = p.variant, schedule = ctx.settings.enable_member_prices && v.schedule;
    const todayKey = today(ctx);
    const day = schedule ? scheduleDay(schedule.days, todayKey) : null;
    const memberEnd = day?.member?.campaignEndDate ? String(day.member.campaignEndDate).slice(0, 10) : '';
    const member = memberEnd !== '' && memberEnd < todayKey ? 0 : Math.round(Number(day?.member?.memberPrice || 0) * 100);
    const row = day?.regular || null;
    const rowCampaignId = digits(row?.ax_campaign_gid || row?.axCampaignGID);
    const rowCampaign = rowCampaignId ? (v.campaigns || []).find(c => digits(c.id) === rowCampaignId) || null : null;
    const regularEnd = row ? String(row.campaignEndDate || rowCampaign?.endDate || '').slice(0, 10) : '';
    const ended = regularEnd !== '' && regularEnd < todayKey;
    const regular = ended ? null : row;
    return {
      schedule, day, regular, campaign: ended ? null : rowCampaign,
      member, memberActive: member > 0 && member < v.price,
      hideSavings: regular?.showSavings === false,
      special: regular?.showSavings === false && Number(regular?.salesUnitPrice) > 0,
      stock: v.price > 0 && regular?.showWhileInStock === true,
    };
  }

  function price(p, ctx, st) {
    const v = p.variant;
    if (typeof v.price !== 'number') return '';
    const sale = v.compareAtPrice > v.price;
    const regular = tr(ctx, 'products.product.price.regular_price');
    const was = `<span class="price__was${ctx.settings.superscript_decimals ? ' price__was--supercents' : ''}"><span class="visually-hidden js-label">${sale ? regular : ''}</span><span class="js-value">${sale ? money(v.compareAtPrice, ctx, false) : ''}</span></span>`;
    const base = `<div class="price${sale ? ' price--on-sale' : ''} price--bottom"${v.price === 0 ? ' hidden' : ''}><div class="price__default"><span class="price__current"><span class="visually-hidden js-label">${sale ? tr(ctx, 'products.product.price.sale_price') : regular}</span><span class="js-value">${money(v.price, ctx)}</span></span>${sale ? `<div class="price__sale-details">${was}<span class="price__savings-text">${esc(text(ctx, 'savings')).replace('__AMT__', money(v.compareAtPrice - v.price, ctx, false))}</span></div>` : was}</div><div class="unit-price relative" hidden><span class="visually-hidden">${tr(ctx, 'products.product.price.unit_price')}</span><span></span></div></div>`;
    if (!st.schedule) return base;
    return `<div class="card-price${st.memberActive ? ' card-price--member' : ''}${st.hideSavings ? ' card-price--hide-savings' : ''}${st.special ? ' card-price--hide-compare' : ''}" data-card-price data-current-variant="${esc(v.id)}" data-savings-label="${esc(text(ctx, 'savings').replace('__AMT__', '').trim())}">${base}<div class="card-price__member" data-card-price-member><span class="card-price__member-primary"><span class="card-price__member-label">${tr(ctx, 'products.product.price.member_price')}</span> <span class="card-price__member-value js-member-value">${st.memberActive ? money(st.member, ctx) : ''}</span></span><span class="card-price__normal"><span class="card-price__normal-label">${tr(ctx, 'products.product.price.non_member_price')}</span> <span class="card-price__normal-value js-normal-value">${st.memberActive ? money(v.price, ctx) : ''}</span></span></div><span class="card-price__stock-note js-stock-note"${st.stock ? '' : ' hidden'}>${tr(ctx, 'products.product.price.while_stocks_last')}</span></div>`;
  }

  // product-label.liquid: priority order, visible limit 3, hidden wrappers
  // don't consume a slot, Mix & Save outside the count.
  function labels(p, ctx, st) {
    const s = ctx.settings, v = p.variant;
    if (s.product_label_card_position === 'none') return '';
    const justify = s.product_label_card_position?.includes('end') ? ' justify-end' : '';
    let count = 0, out = '';
    function add(kind, content, visible = true, attrs = '') {
      if (visible && count >= 3) return false;
      if (visible) count++;
      out += `<div class="product-label-wrapper flex${justify}"${visible ? '' : ' hidden'}><span class="product-label product-label--${kind}"${attrs}>${content}</span></div>`;
      return visible;
    }
    const suffix = p.templateSuffix || '';
    if (s.show_custom_label && p.customBadge?.trim()) add('custom', esc(p.customBadge.trim()));
    if (suffix.includes('preorder')) add('preorder', tr(ctx, 'products.product.preorder'));
    if (s.show_sold_out_label && !p.available) add('sold-out', tr(ctx, 'products.product.sold_out'));
    if (s.show_member_price_label && s.enable_member_prices) add('member', tr(ctx, 'products.product.price.member_price'), st.memberActive && count < 3);
    let campaignShown = false;
    if (s.enable_member_prices && v.campaigns?.length) {
      const row = st.regular, campaign = st.campaign;
      const label = campaign?.label && !st.memberActive && v.price > 0
        ? campaign.label + (Number(row?.savingsPercent) > 0 && row?.showSavings !== false ? ` ${row.savingsPercent}%` : '')
        : '';
      campaignShown = add('campaign', `${campaign?.icon && label ? `<img class="product-label__icon" src="${esc(campaign.icon)}" alt="" width="14" height="14" loading="lazy">` : ''}<span class="product-label__text">${esc(label)}</span>`, Boolean(label) && count < 3, campaign?.color && label ? ` style="--campaign-label-bg-color: ${esc(campaign.color)};"` : '');
    }
    if (s.show_sale_label && v.price > 0 && v.compareAtPrice > v.price && count < 3) {
      const amount = s.sale_label_type === 'percent' ? Math.round((1 - v.price / v.compareAtPrice) * 100) : money(v.compareAtPrice - v.price, ctx, false);
      const template = s.sale_label_type === 'percent' ? text(ctx, 'salePercent') : s.sale_label_type === 'money' ? text(ctx, 'saleMoney') : text(ctx, 'products.product.sale');
      add('sale', `<span class="product-label__text" data-label-type="${esc(s.sale_label_type)}" data-label-template="${esc(template)}">${esc(template).replace('__AMT__', String(amount))}</span>`, !st.memberActive && !campaignShown);
    }
    if (v.fastLowPrice) add('fast-low-price', tr(ctx, 'products.product.fast_low_price'), v.price > 0 && count < 3);
    const config = ctx.mixAndSaveConfiguration, mixTemplate = config?.labelConfig?.labelTemplate || config?.labelTemplate;
    if (s.show_mix_and_save_label && v.mixAndSave && mixTemplate) {
      for (const cfg of config.configurations || []) {
        const pct = v.mixAndSave.discounts?.[String(cfg.id)];
        out += `<mix-and-save-label class="product-label-wrapper flex${justify}" hidden data-cfg-id="${esc(cfg.id)}" data-valid-from="${esc(cfg.validFrom)}" data-valid-to="${esc(cfg.validTo)}" data-template="${esc(mixTemplate.replaceAll('<QUANTITY>', String(cfg.minQuantity)))}"${pct != null ? ` data-pct="${esc(pct)}"` : ''}${v.price === 0 || v.compareAtPrice > v.price ? ' data-suppressed' : ''}${st.memberActive ? ' data-member-suppressed' : ''}><span class="product-label product-label--mix-and-save"><span class="product-label__text"></span></span></mix-and-save-label>`;
      }
    }
    if (s.show_new_label && p.newLabel) add('new', tr(ctx, 'products.product.new'));
    return out ? `<div class="product-label-container absolute ${esc(s.product_label_card_position)} product-label-container--card">${out}</div>` : '';
  }

  // image.liquid for one card image: the width ladder capped at the source
  // width, quality-scaled candidates, packshot marker stripped from the alt.
  function image(img, role, p, ctx) {
    const s = ctx.settings;
    const quality = Number(s.image_quality || 1) * Number(p.media.quality || 1);
    const markers = String(s.packshot_alt_suffix || '').split(',').map(x => x.trim()).filter(Boolean);
    // _packshot-image-class reads the MEDIA alt (packAlt); image.liquid strips
    // the marker from the IMAGE alt — the same string in practice, kept apart
    // because the Liquid keeps them apart.
    const packAlt = img.packAlt ?? img.alt ?? '';
    const isPackshot = markers.some(m => packAlt.endsWith(m));
    let alt = img.alt ?? '';
    const altMarker = markers.find(m => alt.endsWith(m));
    if (altMarker) alt = alt.slice(0, -altMarker.length).trim();
    const width = Number(img.width) || 460;
    const widths = width < 320 ? [width] : [320, 460, 600, 700, 800, 900, 1200].filter(w => w <= width);
    const url = (w) => {
      const u = new URL(img.src, 'https://cdn.invalid');
      u.searchParams.set('width', w);
      return img.src.startsWith('//') ? `//${u.host}${u.pathname}${u.search}` : (img.src.startsWith('/') ? `${u.pathname}${u.search}` : u.href);
    };
    const outWidth = widths[widths.length - 1] * quality;
    const ratio = Number(img.aspectRatio) || 1;
    const fit = s.prod_card_image_fit === 'contain' ? ' img-fit--contain' : '';
    const align = s.prod_card_image_fit === 'contain' ? '' : ` ${s.prod_card_image_align || ''}`;
    // A swipe slide sits in a <template> until the first swipe clones it in, so
    // it loads at once from there (_card-swipe-slides.liquid: lazy_load false).
    return `<img srcset="${esc(widths.map(w => `${url(w * quality)} ${w}w`).join(', '))}" sizes="${esc(ctx.sizes)}" src="${esc(img.src)}" class="img-fit${fit} card__${role}-image${align}${isPackshot ? ' img--packshot' : ''}"${role === 'main' && img.id ? ` data-media-id="${esc(img.id)}"` : ''} loading="${role === 'slide' ? 'eager' : 'lazy'}" width="${outWidth}" height="${Math.round(outWidth / ratio)}" fetchpriority="low" alt="${esc(alt)}">`;
  }

  // _card-swipe-slides.liquid, html output: the hero as slide 1 of the touch
  // swipe strip, slides 2+ inert in <template>s, then the dots; the bare hero
  // when there is no swipe (setting off, no slides).
  const SWIPE_MAX = 5;
  function heroImages(hero, p, ctx) {
    const main = image(hero, 'main', p, ctx);
    const slides = ctx.settings.card_swipe_images ? (p.media?.slides || []).slice(0, SWIPE_MAX - 1) : [];
    if (!slides.length) return main;
    const strip = slides.map(img => `<div class="card__slide"><template>${image(img, 'slide', p, ctx)}</template></div>`).join('');
    const dots = [hero, ...slides].map((_, i) => (i ? '<span></span>' : '<span class="is-active"></span>')).join('');
    return `<div class="card__swipe" tabindex="-1"><div class="card__slide">${main}</div>${strip}</div><div class="card__swipe-dots" aria-hidden="true">${dots}</div>`;
  }

  // _packshot-padding.liquid.
  function packshotPadding(p, ctx) {
    const s = ctx.settings;
    if (!s.packshot_alt_suffix) return '';
    let desktop = Number(s.packshot_default_pad_desktop || 0), mobile = Number(s.packshot_default_pad_mobile || 0);
    const type = String(p.type || '').trim().toLowerCase();
    for (const rule of String(s.packshot_padding_rules || '').split('\n')) {
      const parts = rule.trim().split('|');
      if (parts.length >= 3 && parts[0].trim().toLowerCase() === type) {
        desktop = Number(parts[1].trim());
        mobile = Number(parts[2].trim());
        break;
      }
    }
    return desktop > 0 || mobile > 0 ? ` style="--packshot-pad: ${desktop}%; --packshot-pad-m: ${mobile}%;"` : '';
  }

  // _energy-label.liquid, card context. Mark only when the class maps to an
  // A–G file; links only alongside the mark.
  function energy(p, ctx) {
    if (!ctx.settings.card_show_energy_label || !p.energy?.class) return '';
    const cls = String(p.energy.class).trim().toUpperCase().split(/\s+/).pop();
    const src = ctx.energyFiles?.[cls];
    if (!src) return '';
    const mark = `<img class="product-info__energy-label" src="${esc(src)}" alt="${esc(text(ctx, 'energyLabelAlt').replace('__CLASS__', cls))}" width="472" height="283" loading="lazy" decoding="async">`;
    const linked = p.energy.fullsizeUrl
      ? `<a class="product-info__energy-link" href="${esc(p.energy.fullsizeUrl)}" target="_blank" rel="noopener" aria-label="${tr(ctx, 'products.product.energy_label_open')}">${mark}</a>`
      : mark;
    const sheet = p.energy.datasheetUrl
      ? `<a class="link product-info__energy-datasheet" href="${esc(p.energy.datasheetUrl)}" target="_blank" rel="noopener">${tr(ctx, 'products.product.energy_datasheet')}</a>`
      : '';
    return `<div class="product-info__energy product-info__energy--card">${linked}${sheet}</div>`;
  }

  function quickAdd(p, ctx) {
    const s = ctx.settings, v = p.variant, suffix = p.templateSuffix || '';
    if (!p.purchaseKnown) return '';
    if (!s.enable_quick_add || suffix.toLowerCase().includes('konfigurator')) return '';
    const defaultVariant = p.hasOnlyDefaultVariant && !suffix.includes('coming-soon') && !suffix.includes('countdown');
    const label = tr(ctx, defaultVariant ? (suffix.includes('preorder') ? 'products.product.preorder' : 'products.product.add_to_cart') : 'products.product.choose_options');
    const cls = ['card__quick-add',
      s.card_atc_mobile === 'text_button_bottom' ? 'mob:card__quick-add--below' : s.card_atc_mobile === 'icon' ? 'mob:card__quick-add--icon' : '',
      s.card_atc_desktop === 'text_button_bottom' ? 'desktop:card__quick-add--below' : s.card_atc_desktop === 'icon' ? 'desktop:card__quick-add--icon' : ''].filter(Boolean).join(' ');
    const icon = ctx.icons?.addToCart || '';
    const button = `<button ${defaultVariant ? `data-add-to-cart-text="${label}" name="add"` : `type="button" data-product-url="${esc(p.url)}" data-product-default-variant="${p.hasOnlyDefaultVariant}"`} class="btn btn--primary w-full${defaultVariant ? '' : ' js-quick-add'}" aria-haspopup="dialog"${defaultVariant && (!v.available || v.price === 0) ? ' disabled' : ''}${defaultVariant && v.price === 0 ? ' hidden' : ''}><span class="quick-add-btn-icon${defaultVariant ? '' : ' block pointer-events-none'}"><span class="visually-hidden">${label}</span>${icon}</span><span class="quick-add-btn-text${defaultVariant ? '' : ' block pointer-events-none'}">${label}</span></button><a href="${esc(p.url)}" class="btn btn--secondary quick-add-view-btn">${tr(ctx, 'products.product.view_details')}</a>`;
    const form = defaultVariant
      ? `<product-form><div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div><form method="post" action="${esc(ctx.cartAddUrl || '/cart/add')}" id="quick-add-${esc(p.id)}" accept-charset="UTF-8" class="js-product-form" enctype="multipart/form-data" novalidate="novalidate"><input type="hidden" name="form_type" value="product"><input type="hidden" name="utf8" value="✓"><input type="hidden" name="id" value="${esc(v.id)}">${button}<input type="hidden" name="product-id" value="${esc(p.id)}"><input type="hidden" name="section-id" value=""></form></product-form>`
      : `<div class="no-js-hidden">${button}</div>`;
    // "Hide Quick Buy on products sold out online" (ClickUp 869f9nc39) — product-card.liquid
    // + _card-purchasability.liquid: an unavailable card variant hides the wrapper at once; an
    // available, tracked one gets the element that checks for "Udsolgt online" after load.
    const hideSoldOut = !!s.card_hide_quick_add_sold_out_online;
    const gate = hideSoldOut && v.available && v.inventoryTracked
      ? `<card-purchasability hidden data-variant-id="${esc(v.id)}" data-policy="${esc(v.inventoryPolicy)}" data-target=".card__quick-add"></card-purchasability>`
      : '';
    return `<div class="${cls}"${hideSoldOut && !v.available ? ' hidden' : ''}>${gate}${form}</div>`;
  }

  /**
   * Settings the renderer does not reproduce. Any entry disables client
   * rendering for the whole page (relewise.js falls back to the Shopify card
   * fetch), so a settings change can never silently degrade the cards.
   */
  function unsupportedContext(ctx) {
    const s = ctx?.settings, reasons = [];
    if (!s || ctx.schemaVersion !== 1) return ['context'];
    if (s.card_show_weight) reasons.push('weight');
    if (s.card_show_inventory) reasons.push('inventory');
    if (s.card_show_store_locations) reasons.push('store-locations');
    if (s.card_hover_action === 'slideshow') reasons.push('slideshow');
    if (['swatches', 'variant-images', 'text'].includes(s.card_colors_style)) reasons.push('colour-style');
    for (const key of ['custom_label_icon', 'preorder_label_icon', 'sold_out_label_icon', 'sale_label_icon', 'member_label_icon', 'new_label_icon']) {
      if (s[key] && s[key] !== 'none') reasons.push(key);
    }
    return reasons;
  }

  /** Per-product reasons the model cannot be rendered faithfully. */
  function unsupported(p) {
    const reasons = [...(p.unsupported || [])];
    if (!p.handle) reasons.push('handle');
    if (!p.variant?.id) reasons.push('variant');
    // A missing Relewise price is fine on the provisional model — the background
    // data brings the shop price (relewise.js holds such a card behind its
    // skeleton until then). Only a model that has been merged must have one.
    if (p.purchaseKnown && typeof p.variant?.price !== 'number') reasons.push('price');
    return reasons;
  }

  function html(p, ctx) {
    const s = ctx.settings, st = state(p, ctx);
    const hero = p.media?.hero || null, hover = s.card_hover_action !== 'none' ? p.media?.hover || null : null;
    const title = (s.use_perfion_titles && p.titleLine1) || p.title || '';
    const subtitle = s.use_perfion_titles ? p.titleLine2 : (s.card_show_subtitle ? p.subtitle : '');
    const suffix = p.templateSuffix || '';
    const modifier = suffix.includes('preorder') ? ' card--product-preorder' : suffix.includes('coming-soon') ? ' card--product-coming-soon' : suffix.includes('countdown') ? ' card--product-countdown' : '';
    let ratio = Number(s.prod_card_image_ratio);
    if (!ratio || ratio === 99) ratio = 1;
    const images = hero
      ? heroImages(hero, p, ctx) + (hover ? image(hover, 'hover', p, ctx) : '')
      : (ctx.icons?.placeholder || '');
    const swatches = p.fabricSwatches?.length
      ? `<div class="card__fabric-swatches flex items-center" aria-hidden="true">${p.fabricSwatches.map(u => `<img class="card__fabric-swatch" src="${esc(u)}" width="18" height="18" loading="lazy" alt="">`).join('')}${p.colorCount > p.fabricSwatches.length ? `<span class="card__fabric-more">+${p.colorCount - p.fabricSwatches.length}</span>` : ''}</div><span class="visually-hidden">${esc(p.colorsCountText)}</span>`
      : '';
    return `<div class="rlw-native-card products-grid-container" data-rlw-native-card data-rlw-client-card><product-card class="card card--product hover:grow-media${modifier} h-full${s.card_contain ? ' card--product-contained' : ''}${s.show_dividers ? '' : ' card--no-lines'} relative flex${p.highlight ? ' card--highlight-product' : ''}" data-product-id="${esc(p.id)}"><div class="card__media${hero && hover ? ' has-hover-image' : ''} relative"${packshotPadding(p, ctx)}><a href="${esc(p.url)}" aria-label="${esc(title)}" class="media block relative${s.blend_product_images ? ' image-blend' : ''} js-prod-link" style="padding-top: ${100 / ratio}%;" tabindex="-1">${images}</a>${labels(p, ctx, st)}</div><div class="card__info-container flex flex-col flex-auto relative"><div class="card__info w-full"><div class="card__info-inner flex flex-col h-full w-full">${s.card_show_vendor ? `<p class="card__vendor ${s.show_dividers ? 'mb-1' : 'mb-0'} text-sm text-theme-light">${esc(p.vendor)}</p>` : ''}<p class="card__title font-bold ${s.show_dividers ? 'mb-1' : 'mt-1 mb-0'}"><a href="${esc(p.url)}" class="card-link text-current js-prod-link">${esc(title)}</a></p>${subtitle ? `<p class="card__subtitle text-current mb-1${s.show_dividers ? '' : ' mt-1'}">${esc(subtitle)}</p>` : ''}<div class="card__price-anchor flex grow items-end"><div class="card__price-slot w-full">${price(p, ctx, st)}${energy(p, ctx)}</div></div><div class="card__swatch-slot">${swatches}</div></div></div>${quickAdd(p, ctx)}</div><wishlist-toggle style="display: contents;"><div class="wish__ext" style="height: 0;"><button type="button" class="button-wishlist button-grid" data-button-wishlist aria-label="${tr(ctx, 'wishlist.button.label_add')}" data-product-handle="${esc(p.handle)}" data-variant-id="${esc(p.variant.id)}">${ctx.icons?.wishlist || ''}</button></div></wishlist-toggle></product-card></div>`;
  }

  const api = { html, money, state, scheduleDay, unsupported, unsupportedContext };
  root.RelewiseCards = api;
  if (typeof module !== 'undefined') module.exports = api;
})(typeof window === 'undefined' ? globalThis : window);
